'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Worker, WorkerInput } from '@garage/shared'
import { workerInputSchema, parseMoneyToMinor, formatMinor } from '@garage/shared'
import { Dialog, TextField, Select, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface WorkerFormDialogProps {
  open: boolean
  worker?: Worker
  onClose: () => void
  onCreated?: (worker: Worker) => void
}

interface FormState {
  name: string
  phone: string
  notes: string | null
  salaryDisplay: string
  /** '' means unassigned. */
  departmentId: string
}

export function WorkerFormDialog({ open, worker, onClose, onCreated }: WorkerFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !worker

  const { data: departmentsData } = useQuery({
    queryKey: qk.departments,
    queryFn: () => api.departments.list(),
  })
  const departmentOptions = [
    { value: '', label: t('departmentForm.noDepartment') },
    ...(departmentsData?.items.map((d) => ({ value: d.id, label: d.name })) ?? []),
  ]

  const salaryDisplay = (w?: Worker) =>
    w?.monthlySalaryMinor ? formatMinor(w.monthlySalaryMinor, { decimals: 2, grouping: false }) : ''

  const [formState, setFormState] = useState<FormState>({
    name: worker?.name ?? '',
    phone: worker?.phone ?? '',
    notes: worker?.notes ?? null,
    salaryDisplay: salaryDisplay(worker),
    departmentId: worker?.departmentId ?? '',
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState({
        name: worker?.name ?? '',
        phone: worker?.phone ?? '',
        notes: worker?.notes ?? null,
        salaryDisplay: salaryDisplay(worker),
        departmentId: worker?.departmentId ?? '',
      })
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, worker])

  const mutation = useMutation({
    mutationFn: async (input: WorkerInput) =>
      isCreate ? api.workers.create(input) : api.workers.update(worker!.id, input),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      if (!isCreate) queryClient.invalidateQueries({ queryKey: qk.worker(worker!.id) })
      snackbar.success(isCreate ? t('workerForm.workerAdded') : t('workerForm.workerUpdated'))
      if (isCreate) onCreated?.(saved)
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const parsedSalary = formState.salaryDisplay.trim() ? parseMoneyToMinor(formState.salaryDisplay) : null
    if (formState.salaryDisplay.trim() && parsedSalary === null) {
      setFieldErrors((prev) => ({ ...prev, monthlySalaryMinor: t('workerForm.invalidAmount') }))
      return
    }

    const input: WorkerInput = {
      name: formState.name,
      phone: formState.phone,
      notes: formState.notes,
      monthlySalaryMinor: parsedSalary,
      departmentId: formState.departmentId || null,
    }

    const result = workerInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('staff.addWorker') : t('staff.editWorker')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TextField
          label={t('inspectionTemplate.name')}
          value={formState.name}
          onChange={(v) => setFormState((s) => ({ ...s, name: v }))}
          error={fieldErrors.name}
          fullWidth
          autoFocus
        />
        <TextField
          label={t('clientForm.phoneOptional')}
          value={formState.phone}
          onChange={(v) => setFormState((s) => ({ ...s, phone: v }))}
          error={fieldErrors.phone}
          fullWidth
        />
        <TextField
          label={t('workerForm.monthlySalaryOptional')}
          value={formState.salaryDisplay}
          onChange={(v) => setFormState((s) => ({ ...s, salaryDisplay: v }))}
          error={fieldErrors.monthlySalaryMinor}
          supportingText={t('workerForm.monthlySalaryHelp')}
          fullWidth
        />
        <Select
          label={t('workerForm.department')}
          value={formState.departmentId}
          onChange={(v) => setFormState((s) => ({ ...s, departmentId: v }))}
          options={departmentOptions}
          error={fieldErrors.departmentId}
          fullWidth
        />
        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
